import React from "react"

import {
    css,
    Grid,
    GridCell,
    Toggler,
} from "@bdfx/mergado-ui-kit"
import { STAR_SYMBOLS } from "shape-entities/components/rating-svg"
import trans from "trans"
import { Slider } from "@mui/material"
import { iconDisabledColor } from "template-edit/components/tools-panel"
import TransparentColor from "./transparent-color"
import ImagickFontSelect from "shape-edit/components/imagickFontSelect"
import TextOptionsForm from "./text-options-form"
import { StyledSeparatorNoLine } from "./common-shape-form"
import { NumericInput } from "./fields"

const { min, max, abs, sin, cos, PI, round, atan2, sqrt, ceil } = Math

const RatingForm = ({ formName, shape, activeShapeId, change, updateShapeSizeDebounced }) => {
    const subFormName = `shapes.${shape.index}`
    return (
        <React.Fragment>
            <GridCell>
                <Grid cols={'30% 70%'} gap={'10px'} style={{ ' .muk-gridcell': { alignSelf: 'center' } }}>
                    <GridCell>{trans('star_symbol')}</GridCell>
                    <StyledGrid>
                        {STAR_SYMBOLS.map((starPath, idx) => <GridCell
                            key={`button_${idx}`}
                            title={`${idx}`}
                            className={shape.star_symbol === idx ? 'active' : ''}
                            onClick={() => {
                                change(formName, `${subFormName}.star_symbol`, idx)
                                updateShapeSizeDebounced(subFormName, shape.index, true, true)
                            }}>
                            <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 40 40' xmlnsXlink='http://www.w3.org/1999/xlink'>
                                <path
                                    fill={shape.color}
                                    stroke={shape.color}
                                    strokeWidth={shape.stroke_width}
                                    d={starPath}
                                />
                            </svg>
                        </GridCell>)}
                    </StyledGrid>

                    <GridCell>{trans('rating_min_stars')}</GridCell>
                    <GridCell style={{ alignSelf: 'end', padding: '0 8px' }}>
                        <StyledSliderDiv>
                            <Slider
                                value={shape.rating_min_stars}
                                onChange={(event: Event, value: number) => {
                                    change(formName, `shapes.${activeShapeId}.rating_min_stars`, value)
                                }}
                                step={0.1}
                                min={0}
                                max={5}
                                color={'primary'}
                                marks={[
                                    { value: 1, label: '1' },
                                    { value: 2, label: '2' },
                                    { value: 3, label: '3' },
                                    { value: 4, label: '4' },
                                    { value: 5, label: '5' },
                                ]}
                                valueLabelDisplay={shape.rating_min_stars ? "on" : 'auto'}
                                getAriaLabel={() => `${trans('rating_min_stars')}`}
                                getAriaValueText={(value) => `${value}`}
                                valueLabelFormat={(value) => `${value}`}
                            />
                        </StyledSliderDiv>
                    </GridCell>

                    <GridCell col={'1/-1'}>
                        <Grid cols={'auto max-content'}>
                            <GridCell onClick={() => change(formName, `shapes.${activeShapeId}.rating_hide_empty`, !shape.rating_hide_empty)}>
                                {trans('rating_hide_empty')}
                            </GridCell>
                            <StyledToggler
                                name={`${formName}.shapes.${shape.index}.rating_hide_empty`}
                                value={shape.rating_hide_empty || false}
                            />
                        </Grid>
                    </GridCell>

                    <GridCell>{trans('shape_color')}</GridCell>
                    <GridCell>
                        <TransparentColor
                            name={`${formName}.${subFormName}.color`}
                            value={shape.color}
                            notRequired={false}
                            changeValue={(color: string) => change(formName, `${subFormName}.color`, color)}
                        />
                    </GridCell>

                    <GridCell>{trans('text_background_color')}</GridCell>
                    <GridCell>
                        <TransparentColor
                            name={`${formName}.${subFormName}.text_background_color`}
                            value={shape.text_background_color}
                            notRequired={true}
                            changeValue={(color: string) => change(formName, `${subFormName}.text_background_color`, color)}
                        />
                    </GridCell>

                    <GridCell>{trans('corner_radius')}</GridCell>
                    <GridCell>
                        <NumericInput
                            formName={formName}
                            fieldName={'corner_radius'}
                            placeholder={'corner_radius'}
                            shapeIndex={shape.index}
                            id={'corner_radius'}
                            min={0}
                            max={100}
                            step={1}
                            units={'px'}
                            value={round(shape.corner_radius)}
                            onlyPositive={true}
                        />
                    </GridCell>

                    <GridCell>{trans('text_padding')}</GridCell>
                    <GridCell>
                        <NumericInput
                            formName={formName}
                            fieldName={'text_padding'}
                            id={'text_padding'}
                            style={{ width: 'auto' }}
                            min={0}
                            max={round(min(shape.width, shape.height) / 2)}
                            step={1}
                            units={'px'}
                            value={shape.text_padding}
                            onlyPositive={true}
                            changeValueMethod={(value: number) => {
                                change(formName, `shapes.${activeShapeId}.text_padding`, value)
                                //kdyz pridam padding, nechat width, height, zmenit fontsize!
                                updateShapeSizeDebounced(subFormName, activeShapeId, false)
                                return value
                            }}
                        />
                    </GridCell>
                </Grid>
            </GridCell>

            <GridCell />

            <ImagickFontSelect
                currentFontFamily={shape.text_fontfamily}
                setFontFamily={(value: string) => {
                    change(formName, `shapes.${activeShapeId}.text_fontfamily`, value)
                    updateShapeSizeDebounced(subFormName, activeShapeId, false)
                }}
                sampleText={(shape.text_content_replaced) ? shape.text_content_replaced : (shape.text_content ?? trans("font_example"))}
            />

            <TextOptionsForm
                shape={shape}
                activeShapeId={activeShapeId}
                formName={formName}
                change={change}
                updateShapeSizeDebounced={updateShapeSizeDebounced}
            />

            <GridCell><StyledSeparatorNoLine /></GridCell>
        </React.Fragment>
    )
}

export const StyledSliderDiv = css('div')({
    position: 'relative',
    padding: '0 25px',
    ' .MuiSlider-colorSecondary': {
        color: iconDisabledColor,
        ' .MuiSlider-track': {
            backgroundColor: iconDisabledColor,
            borderColor: iconDisabledColor,
        }
    },
})

export const StyledToggler = css(Toggler)({
    ' .muk-grid': {
        gridTemplateColumns: 'auto auto',
        width: '100%',
    },
})

const StyledGrid = css(Grid)({
    gridTemplateColumns: 'repeat(auto-fit, minmax(40px, 1fr))',
    gridAutoFlow: 'row !important',
    gridGap: '5px',
    ' .muk-gridcell': {
        textAlign: 'center',
        alignItems: 'center',
        padding: '6px 0 0 0',
    },
    ' .active': {
        border: `1px solid #cccccc`,
        borderRadius: '4px',
    }
})

export default RatingForm